/**
 * MamaLink - Composant de partage social
 * Permet de partager du contenu sur les réseaux sociaux
 * Inclut: Facebook, Twitter, WhatsApp, Telegram, Instagram, TikTok
 */
import React, { useState } from 'react';
import { Share2, Facebook, Twitter, MessageCircle, Link2, Check, X, Instagram, Copy } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 📊 Fire-and-forget : track un partage social pour les analytics admin ET débloque
// automatiquement le badge social correspondant si la plateforme y est éligible.
// On ignore les erreurs car ça ne doit jamais bloquer le partage.
// 🎯 Élodie #1024 — bug corrigé : partages FB/Twitter/etc. ne débloquaient plus le badge
// car SocialShare appelait /analytics/share-event (analytics only) mais oubliait
// /badges/social-share (badge unlock). Les 2 endpoints tirent désormais en parallèle.
const BADGE_ELIGIBLE_PLATFORMS = new Set([
  'facebook', 'instagram', 'whatsapp', 'tiktok', 'twitter', 'telegram', 'signal',
]);

const trackShareEvent = (platform, context = null) => {
  try {
    const token = localStorage.getItem('mamalink_token') || sessionStorage.getItem('mamalink_token');
    if (!token) return;
    // 1. Analytics
    axios.post(`${API}/analytics/share-event`,
      { platform, context },
      { headers: { Authorization: `Bearer ${token}` } }
    ).catch(() => {});
    // 2. Badge unlock (uniquement pour les 7 plateformes badge-éligibles)
    if (BADGE_ELIGIBLE_PLATFORMS.has((platform || '').toLowerCase())) {
      axios.post(`${API}/badges/social-share?network=${encodeURIComponent(platform.toLowerCase())}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      ).then((res) => {
        if (res?.data?.badge_name && !res.data.already_earned) {
          try {
            // Toast si dispo (sonner injecté globalement) — sinon silencieux
            if (typeof window !== 'undefined' && window.__sonner_toast) {
              window.__sonner_toast(`Nouveau badge : ${res.data.badge_name} 🎉`);
            }
          } catch (_) { /* noop */ }
        }
      }).catch(() => {});
    }
  } catch (_) { /* noop */ }
};

// TikTok icon component (not in lucide-react)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const SocialShare = ({ 
  title = "MamaLink", 
  text = "Rejoins MamaLink, l'app des mamans !", 
  url = "https://mamalink.fr",
  hashtags = ["MamaLink", "Mamans", "Maternité"],
  showLabel = true,
  variant = "button", // "button" | "icons" | "modal"
  className = "",
  referralCode = null // Code de parrainage optionnel
}) => {
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showInstaHelp, setShowInstaHelp] = useState(false);

  // Add referral code to URL if provided
  const shareUrl = referralCode ? `${url}?ref=${referralCode}` : url;

  const shareData = {
    title,
    text,
    url: shareUrl
  };

  // Check if native share is available (mobile)
  const canNativeShare = typeof navigator !== 'undefined' && navigator.share;

  // Handle native share
  const handleNativeShare = async () => {
    try {
      await navigator.share(shareData);
      trackShareEvent('native', title);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share error:', error);
        setShowModal(true); // Fallback to modal
      }
    }
  };

  // Copy link to clipboard
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      trackShareEvent('copy-link', title);
    } catch (error) {
      console.error('Copy error:', error);
    }
  };

  // Copy text for Instagram/TikTok (they don't support direct sharing)
  const copyForSocial = async (platform) => {
    const socialText = `${text}\n\n${hashtags.map(h => `#${h}`).join(' ')}\n\n${shareUrl}`;
    try {
      await navigator.clipboard.writeText(socialText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      trackShareEvent(platform, title);

      if (platform === 'instagram') {
        setShowInstaHelp(true);
        // 🎯 Élodie #1012 — instagram://app scheme → page vide si app pas installée.
        // On utilise le lien web qui redirige vers l'app si installée, sinon web IG.
        // Le texte est déjà dans le presse-papier, la maman n'a qu'à coller.
        setTimeout(() => {
          window.location.href = 'https://www.instagram.com/create/story/';
        }, 800);
      } else if (platform === 'tiktok') {
        // 🎯 Élodie #1012 — Idem pour TikTok. Le scheme tiktok://app ouvrait l'app
        // sans destination utile. On envoie vers la page upload qui deep-link
        // automatiquement dans l'app TikTok si installée.
        setShowInstaHelp(true);
        setTimeout(() => {
          window.location.href = 'https://www.tiktok.com/upload?lang=fr';
        }, 800);
      }
    } catch (error) {
      console.error('Copy error:', error);
    }
  };

  // Share URLs for different platforms
  const shareUrls = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(text)}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}&hashtags=${hashtags.join(',')}`,
    whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(`${text}\n${shareUrl}`)}`,
    telegram: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`
  };

  const openShareWindow = (platform) => {
    const shareUrlPlatform = shareUrls[platform];
    trackShareEvent(platform, title);
    window.open(shareUrlPlatform, '_blank', 'width=600,height=400,menubar=no,toolbar=no');
  };

  // Share button click handler
  const handleShareClick = () => {
    if (canNativeShare) {
      handleNativeShare();
    } else {
      setShowModal(true);
    }
  };

  // Render icon-only buttons
  if (variant === "icons") {
    return (
      <div className={`flex items-center gap-2 flex-wrap ${className}`}>
        <button
          onClick={() => openShareWindow('facebook')}
          className="w-10 h-10 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:opacity-90 transition-opacity"
          aria-label="Partager sur Facebook"
          data-testid="share-facebook"
        >
          <Facebook className="w-5 h-5" />
        </button>
        <button
          onClick={() => openShareWindow('twitter')}
          className="w-10 h-10 rounded-full bg-[#1DA1F2] text-white flex items-center justify-center hover:opacity-90 transition-opacity"
          aria-label="Partager sur Twitter"
          data-testid="share-twitter"
        >
          <Twitter className="w-5 h-5" />
        </button>
        <button
          onClick={() => openShareWindow('whatsapp')}
          className="w-10 h-10 rounded-full bg-[#25D366] text-white flex items-center justify-center hover:opacity-90 transition-opacity"
          aria-label="Partager sur WhatsApp"
          data-testid="share-whatsapp"
        >
          <MessageCircle className="w-5 h-5" />
        </button>
        <button
          onClick={() => copyForSocial('instagram')}
          className="w-10 h-10 rounded-full bg-gradient-to-br from-[#f09433] via-[#e6683c] to-[#dc2743] text-white flex items-center justify-center hover:opacity-90 transition-opacity"
          aria-label="Partager sur Instagram"
          data-testid="share-instagram"
        >
          <Instagram className="w-5 h-5" />
        </button>
        <button
          onClick={() => copyForSocial('tiktok')}
          className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center hover:opacity-90 transition-opacity"
          aria-label="Partager sur TikTok"
          data-testid="share-tiktok"
        >
          <TikTokIcon className="w-5 h-5" />
        </button>
        <button
          onClick={copyToClipboard}
          className={`w-10 h-10 rounded-full ${copied ? 'bg-[#B5E48C]' : 'bg-[#9D8189]'} text-white flex items-center justify-center hover:opacity-90 transition-all`}
          aria-label="Copier le lien"
          data-testid="share-copy"
        >
          {copied ? <Check className="w-5 h-5" /> : <Link2 className="w-5 h-5" />}
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Share Button */}
      <button
        onClick={handleShareClick}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#FFB5A7] to-[#FCD5CE] text-white hover:shadow-lg transition-all ${className}`}
        data-testid="share-button"
      >
        <Share2 className="w-5 h-5" />
        {showLabel && <span className="font-medium">Partager</span>}
      </button>

      {/* Share Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => { setShowModal(false); setShowInstaHelp(false); }}>
          <div 
            className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl animate-fade-in max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-[#5C3D36]">Partager</h3>
              <button 
                onClick={() => { setShowModal(false); setShowInstaHelp(false); }}
                className="p-2 rounded-full hover:bg-[#F8EDEB]"
                data-testid="share-modal-close"
              >
                <X className="w-5 h-5 text-[#9D8189]" />
              </button>
            </div>

            {/* Share preview */}
            <div className="bg-[#F8EDEB] rounded-xl p-4 mb-6">
              <p className="text-sm text-[#5C3D36] font-medium">{title}</p>
              <p className="text-xs text-[#9D8189] mt-1 line-clamp-2">{text}</p>
              {referralCode && (
                <p className="text-xs text-[#FFB5A7] mt-2 font-medium">🎁 Code parrainage : {referralCode}</p>
              )}
            </div>

            {/* Instagram/TikTok help message */}
            {showInstaHelp && (
              <div className="bg-gradient-to-r from-[#f09433]/10 to-[#dc2743]/10 rounded-xl p-4 mb-4 border border-[#dc2743]/20">
                <p className="text-sm text-[#5C3D36] font-medium mb-1">✨ Texte copié !</p>
                <p className="text-xs text-[#9D8189]">
                  Colle le texte dans ta story ou ta bio Instagram/TikTok pour partager MamaLink avec tes amies !
                </p>
              </div>
            )}

            {/* Share options - Row 1 */}
            <div className="grid grid-cols-4 gap-4 mb-4">
              <button
                onClick={() => openShareWindow('facebook')}
                className="flex flex-col items-center gap-2"
                data-testid="modal-share-facebook"
              >
                <div className="w-14 h-14 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:opacity-90 transition-opacity">
                  <Facebook className="w-6 h-6" />
                </div>
                <span className="text-xs text-[#9D8189]">Facebook</span>
              </button>

              <button
                onClick={() => openShareWindow('twitter')}
                className="flex flex-col items-center gap-2"
                data-testid="modal-share-twitter"
              >
                <div className="w-14 h-14 rounded-full bg-[#1DA1F2] text-white flex items-center justify-center hover:opacity-90 transition-opacity">
                  <Twitter className="w-6 h-6" />
                </div>
                <span className="text-xs text-[#9D8189]">Twitter</span>
              </button>

              <button
                onClick={() => openShareWindow('whatsapp')}
                className="flex flex-col items-center gap-2"
                data-testid="modal-share-whatsapp"
              >
                <div className="w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center hover:opacity-90 transition-opacity">
                  <MessageCircle className="w-6 h-6" />
                </div>
                <span className="text-xs text-[#9D8189]">WhatsApp</span>
              </button>

              <button
                onClick={() => openShareWindow('telegram')}
                className="flex flex-col items-center gap-2"
                data-testid="modal-share-telegram"
              >
                <div className="w-14 h-14 rounded-full bg-[#0088cc] text-white flex items-center justify-center hover:opacity-90 transition-opacity">
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                  </svg>
                </div>
                <span className="text-xs text-[#9D8189]">Telegram</span>
              </button>
            </div>

            {/* Share options - Row 2 (Instagram & TikTok) */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <button
                onClick={() => copyForSocial('instagram')}
                className="flex flex-col items-center gap-2"
                data-testid="modal-share-instagram"
              >
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#f09433] via-[#e6683c] to-[#dc2743] text-white flex items-center justify-center hover:opacity-90 transition-opacity">
                  <Instagram className="w-6 h-6" />
                </div>
                <span className="text-xs text-[#9D8189]">Instagram</span>
              </button>

              <button
                onClick={() => copyForSocial('tiktok')}
                className="flex flex-col items-center gap-2"
                data-testid="modal-share-tiktok"
              >
                <div className="w-14 h-14 rounded-full bg-black text-white flex items-center justify-center hover:opacity-90 transition-opacity">
                  <TikTokIcon className="w-6 h-6" />
                </div>
                <span className="text-xs text-[#9D8189]">TikTok</span>
              </button>

              {/* Empty slots for alignment */}
              <div></div>
              <div></div>
            </div>

            {/* Copy link */}
            <div className="flex items-center gap-2 bg-[#F8EDEB] rounded-xl p-3">
              <input
                type="text"
                value={shareUrl}
                readOnly
                className="flex-1 bg-transparent text-sm text-[#5C3D36] truncate outline-none"
              />
              <button
                onClick={copyToClipboard}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  copied 
                    ? 'bg-[#B5E48C] text-white' 
                    : 'bg-[#FFB5A7] text-white hover:bg-[#FFA090]'
                }`}
                data-testid="modal-copy-link"
              >
                {copied ? 'Copié !' : 'Copier'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SocialShare;
